import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROUTE_PATHS } from '@/app/router/paths';

import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '../types/auth.types';

export interface RequireAuthProps {
  /**
   * When set, the user must also hold one of these roles. Omit for routes
   * that any signed-in user may open.
   */
  allowedRoles?: readonly UserRole[];
}

/**
 * Route guard applied once at the layout level, so individual pages never
 * repeat an auth check.
 *
 * SECURITY: this controls what the UI renders, nothing more. The protected
 * page's data still arrives from API calls the backend authorizes
 * independently — a user who edits Redux state to fake a role gets the layout
 * but not the data.
 */
export function RequireAuth({ allowedRoles }: RequireAuthProps) {
  const { isAuthenticated, can } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there. The
    // value is re-validated as a same-origin path before it is used.
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={ROUTE_PATHS.login} state={{ from }} replace />;
  }

  if (allowedRoles && !can(allowedRoles)) {
    return <Navigate to={ROUTE_PATHS.forbidden} replace />;
  }

  return <Outlet />;
}
